import React, { ReactNode, useMemo } from 'react'

import { FloatingArrow } from '@floating-ui/react'

import { mainColors, semanticColors } from '../../styles'
import { FloatingContainer, Placement } from '../FloatingContainer'
import * as S from './Tooltip.styles'

export type TooltipTrigger = 'hover' | 'click' | 'focus' | 'manual'

export enum TooltipTheme {
  light = 'light',
  dark = 'dark',
  danger = 'danger',
  alert = 'alert',
}

export interface TooltipProps {
  className?: string
  children: ReactNode
  content: ReactNode | (() => ReactNode)
  placement?: Placement
  theme?: TooltipTheme
  arrow?: boolean
  interactive?: boolean
  disabled?: boolean
  maxWidth?: number | string
  followCursor?: boolean
  isVisible?: boolean
  visible?: boolean // controlled visibility similar to tippy's visible
  offset?: number | [number, number] // [skidding, distance] like Tippy
  animation?: boolean // enable/disable enter/exit animation
  onShow?: () => void // callback when tooltip shows
  onHide?: (() => void) | (() => boolean) // callback when tooltip hides, return false to prevent
  trigger?: TooltipTrigger | TooltipTrigger[] // trigger mode(s): 'hover', 'click', 'focus', 'manual' (open controlled by the caller)
  hideOnClick?: boolean // hide tooltip when clicking the trigger (default: true for hover, false for click trigger)
}

export const Tooltip = ({
  className,
  children,
  content,
  placement = 'right',
  theme = TooltipTheme.dark,
  arrow = true,
  interactive = false,
  disabled = false,
  maxWidth,
  followCursor = false,
  isVisible = true,
  visible,
  offset: offsetProp = 10,
  animation = false,
  onShow,
  onHide,
  trigger = 'hover',
  hideOnClick,
}: TooltipProps): JSX.Element => {
  const baseContent = useMemo(
    () => (typeof content === 'function' ? (content as () => ReactNode)() : content),
    [content],
  )

  const disabledAll = disabled || !isVisible || !baseContent

  return (
    <FloatingContainer
      placement={placement}
      arrow={arrow}
      interactive={interactive}
      disabled={disabledAll}
      followCursor={followCursor}
      open={visible}
      onShow={onShow}
      onHide={onHide}
      trigger={trigger as any}
      hideOnClick={hideOnClick}
      offset={offsetProp}
      portal
      positioningTarget={children}
    >
      {({ context, arrowRef }) => (
        <S.Container
          className={className}
          $theme={theme}
          $maxWidth={maxWidth}
          $animation={animation}
        >
          {baseContent}
          {arrow && (
            <FloatingArrow
              // @ts-ignore
              ref={arrowRef}
              context={context}
              width={12}
              height={6}
              tipRadius={0}
              fill={
                theme === TooltipTheme.dark
                  ? mainColors.primaryMedium
                  : theme === TooltipTheme.light
                    ? mainColors.white
                    : theme === TooltipTheme.danger
                      ? semanticColors.failureExtraLight
                      : theme === TooltipTheme.alert
                        ? semanticColors.warningExtraLight
                        : mainColors.white
              }
              stroke={
                theme === TooltipTheme.light
                  ? mainColors.neutralLight
                  : theme === TooltipTheme.dark
                    ? mainColors.primaryMedium
                    : theme === TooltipTheme.danger
                      ? semanticColors.failureExtraLight
                      : theme === TooltipTheme.alert
                        ? semanticColors.warningExtraLight
                        : mainColors.white
              }
              strokeWidth={1}
            />
          )}
        </S.Container>
      )}
    </FloatingContainer>
  )
}
